<template>
  <div v-if="dataFinishedLoading" id="createEditStatusReport">
    <Stepper
      :current-step="currentStepper"
      :step-list="stepperList"
      :disabled-steps="disabledStepperItems"
      @selectedItem="setStepperItem($event)"
    ></Stepper>

    <h3 id="title">{{ $t(localeConsts.createEditStatusReportTitle) }} {{ projectWeek }}</h3>

    <ValidationObserver v-slot="{ invalid }">
      <form class="form">
        <ValidationProvider
          v-for="(field, index) in currentStep.fields"
          :key="field.name"
          v-slot="{ errors }"
          :name="field.name"
          :rules="field.rules"
          persist
        >
          <div v-if="index == 0" class="formHeader">
            <span class="currentStepTitle">
              <h5>{{ $t(currentStep.title) }}:</h5>
            </span>

            <StatusReportStatusButtons
              v-if="currentStep.statusButtons && index == 0"
              :good-status-explanation="currentStep.statusButtonsGoodExplanation"
              :improvable-status-explanation="currentStep.statusButtonsImprovableExplanation"
              :bad-status-explanation="currentStep.statusButtonsBadExplanation"
              :selected="currentStep.selectedStatus"
              @selectedStatusEvent="currentStep.selectedStatus = $event"
            ></StatusReportStatusButtons>
          </div>

          <div class="inputs">
            <TextArea
              v-if="field.textArea"
              :name="$t(field.title)"
              :value="field.value"
              :placeholder="$t(field.placeholder)"
              :rows="field.rows"
              @input="field.value = $event"
            >
            </TextArea>

            <StatusReportRiskTable
              v-if="currentStep.table && index == 0"
              id="riskTable"
              :risks="currentStep.fields"
              @input="currentStep.fields = risk"
            ></StatusReportRiskTable>

            <span class="stepErrorMessages">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <div class="stepButtons">
          <PreviousStepButton v-if="activeStep > 0" @clickEvent="previousStep"> </PreviousStepButton>

          <SubmitButton v-if="isLastStep" :disabled="invalid" @clickEvent="createEditStatusReport"> </SubmitButton>

          <NextStepButton v-else :disabled="invalid" @clickEvent="nextStep"> </NextStepButton>
        </div>

        <b-alert id="successMessage" :show="dismissCountDown" fade variant="success" @dismiss-count-down="countDownChanged">
          {{ $t(localeConsts.successMessage) }}
        </b-alert>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import Stepper from '@/components/util/Stepper'

import TextArea from '@/components/util/Inputs/TextArea'

import NextStepButton from '@/components/util/Buttons/FormButtons/NextStepButton'
import PreviousStepButton from '@/components/util/Buttons/FormButtons/PreviousStepButton'
import SubmitButton from '@/components/util/Buttons/FormButtons/SubmitButton'
import StatusReportStatusButtons from './StatusReportStatusButtons'

import StatusReportRiskTable from './StatusReportRiskTable'

import { multiFormMixin } from '../../../mixins/multiFormMixin'
import { generalHelperMethodsMixin } from '../../../mixins/generalHelperMethodsMixin'
import { alertMessagesMixin } from '../../../mixins/alertMessagesMixin'

import { apiFetchMethodsMixin } from '../../../mixins/apiFetchMethodsMixin'
import mapGettersMixin from '../../../mixins/mapGettersMixin'

import axios from '../../../../axios'

export default {
  name: 'CreateEditStatusReport',
  components: { Stepper, TextArea, StatusReportStatusButtons, StatusReportRiskTable, NextStepButton, PreviousStepButton, SubmitButton },
  mixins: [multiFormMixin, generalHelperMethodsMixin, mapGettersMixin, alertMessagesMixin, apiFetchMethodsMixin],

  data() {
    const getInitialStepsData = () => [
      {
        title: 'STATUSREPORTS.OVERALL_STATUS',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.OVERALL_STATUS_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.OVERALL_STATUS_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.OVERALL_STATUS_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'overallStatus',
            placeholder: 'STATUSREPORTS.OVERALL_STATUS_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.WORK_LOAD',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.WORKLOAD_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.WORKLOAD_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.WORKLOAD_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'workLoad',
            placeholder: 'STATUSREPORTS.WORKLOAD_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.QUALITY',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.QUALITY_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.QUALITY_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.QUALITY_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'quality',
            placeholder: 'STATUSREPORTS.QUALITY_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.TEAM_SPIRIT',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.TEAM_SPIRIT_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.TEAM_SPIRIT_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.TEAM_SPIRIT_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'teamSpirit',
            placeholder: 'STATUSREPORTS.TEAM_SPIRIT_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.SCHEDULE',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.SCHEDULE_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.SCHEDULE_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.SCHEDULE_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'schedule',
            placeholder: 'STATUSREPORTS.SCHEDULE_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.BUDGET',
        statusButtons: true,
        statusButtonsGoodExplanation: 'STATUSREPORTS.BUDGET_GOOD_EXPLANATION',
        statusButtonsImprovableExplanation: 'STATUSREPORTS.BUDGET_IMPROVABLE_EXPLANATION',
        statusButtonsBadExplanation: 'STATUSREPORTS.BUDGET_BAD_EXPLANATION',
        selectedStatus: 0,
        fields: [
          {
            name: 'budget',
            placeholder: 'STATUSREPORTS.BUDGET_PLACEHOLDER',
            rows: 6,
            rules: 'required|max:1024',
            value: '',
            textArea: true
          }
        ]
      },
      {
        title: 'STATUSREPORTS.LIST_CURRENT_RISKS',
        table: true,
        fields: [
          {
            name: 'risk1',
            riskId: -1,
            identifiedRisks: '',
            cause: '',
            counterMeasures: '',
            consequences: ''
          }
        ]
      }
    ]

    return {
      localeConsts: {
        createEditStatusReportTitle: 'STATUSREPORTS.NEW_STATUSREPORT_TITLE_PW',
        successMessage: 'ALERT.STATUSPORT_SUCCESSFULLY_CREATED_CHANGED'
      },
      statusReportId: -1,
      projectWeek: this.$store.getters['authentication/getCurrentWeek'],
      currentStepper: 1,
      stepperList: [
        'STATUSREPORTS.OVERALL_STATUS',
        'STATUSREPORTS.WORK_LOAD',
        'STATUSREPORTS.QUALITY',
        'STATUSREPORTS.TEAM_SPIRIT',
        'STATUSREPORTS.SCHEDULE',
        'STATUSREPORTS.BUDGET',
        'STATUSREPORTS.RISKS'
      ],
      disabledStepperItems: [false, true, true, true, true, true, true],
      activeStep: 0,
      steps: getInitialStepsData(),
      getInitialStepsDataInternal: getInitialStepsData,
      dataFinishedLoading: false,
      dismissCountDown: 0
    }
  },

  watch: {
    async $route(to, from) {
      if (to.name === 'CreateEditStatusReport') {
        if (this.getIsSupervisor) {
          this.$router.push({ name: 'StatusReports' })
          return
        }
        this.dataFinishedLoading = false
        if (to.params.id) {
          this.statusReportId = parseInt(to.params.id, 10)
          await this.fetchStatusReportDataById()
        } else {
          this.initializeCreateModeState()
        }
        this.dataFinishedLoading = true
      }
    }
  },

  async created() {
    if (this.getIsSupervisor) {
      this.$router.push({ name: 'StatusReports' })
      return
    }
    this.dataFinishedLoading = false
    if (this.$route.params.id) {
      this.statusReportId = parseInt(this.$route.params.id, 10)
      await this.fetchStatusReportDataById()
    } else {
      this.initializeCreateModeState()
    }
    this.dataFinishedLoading = true
  },

  methods: {
    getInitialStepsState() {
      return JSON.parse(JSON.stringify(this.getInitialStepsDataInternal()))
    },

    initializeCreateModeState() {
      this.statusReportId = -1
      this.steps = this.getInitialStepsState()
      this.currentStepper = 1
      this.activeStep = 0
      this.disabledStepperItems = [false, true, true, true, true, true, true]
      this.dismissCountDown = 0
      if (this.$refs.observer) {
        this.$refs.observer.reset()
      }
    },

    async loadData() {
      await this.getStatusReportsAndFilterByWeek()
      return true
    },

    /*api call methods*/

    async getStatusReportsAndFilterByWeek() {
      await this.fetchStatusReportData()

      this.filterOnWeek(this.projectWeek)
      this.setStatusReport()

      //only search for risks if a status report for the current project week exists
      if (this.statusReportWasAlreadyCreated()) {
        await this.fetchStatusReportRisks()
        this.setIdentifiedStatusReportRisks()
      }
    },

    async fetchStatusReportDataById() {
      const response = await axios.get(`status-reports/${this.statusReportId}/`)
      this.fetchedStatusReportData = [response.data]
      this.projectWeek = this.getWeekNumber(response.data.creation_date)
      this.setStatusReport()
      await this.fetchStatusReportRisks()
      this.setIdentifiedStatusReportRisks()
    },

    /*api write methods*/

    async createEditStatusReport() {
      const responseProjectId = await this.getProjectId

      if (!this.statusReportWasAlreadyCreated()) {
        const newStatusReport = {
          author_student: this.getUserId,
          project: responseProjectId,

          global_status_level: parseInt(this.steps[0].selectedStatus),
          global_status_comments: this.steps[0].fields[0].value,

          workload_level: parseInt(this.steps[1].selectedStatus),
          workload_comments: this.steps[1].fields[0].value,

          quality_level: parseInt(this.steps[2].selectedStatus),
          quality_comments: this.steps[2].fields[0].value,

          team_spirit_level: parseInt(this.steps[3].selectedStatus),
          team_spirit_comments: this.steps[3].fields[0].value,

          schedule_level: parseInt(this.steps[4].selectedStatus),
          schedule_comments: this.steps[4].fields[0].value,

          budget_level: parseInt(this.steps[5].selectedStatus),
          budget_comments: this.steps[5].fields[0].value,

          identified_risks: []
        }

        axios.post('status-reports/', newStatusReport).then((response) => {
          this.statusReportId = response.data.id
          this.createEditIdentifiedRisks()
        })
      } else {
        const patchStatusReport = {
          global_status_level: parseInt(this.steps[0].selectedStatus),
          global_status_comments: this.steps[0].fields[0].value,

          workload_level: parseInt(this.steps[1].selectedStatus),
          workload_comments: this.steps[1].fields[0].value,

          quality_level: parseInt(this.steps[2].selectedStatus),
          quality_comments: this.steps[2].fields[0].value,

          team_spirit_level: parseInt(this.steps[3].selectedStatus),
          team_spirit_comments: this.steps[3].fields[0].value,

          schedule_level: parseInt(this.steps[4].selectedStatus),
          schedule_comments: this.steps[4].fields[0].value,

          budget_level: parseInt(this.steps[5].selectedStatus),
          budget_comments: this.steps[5].fields[0].value
        }

        axios.patch(`status-reports/${this.statusReportId}/`, patchStatusReport)
        this.createEditIdentifiedRisks()
      }
    },

    createEditIdentifiedRisks() {
      for (let i = 0; i < this.steps[6].fields.length; i++) {
        const riskId = this.steps[6].fields[i].riskId

        if (!this.identifiedRiskWasAlreadyCreated(riskId)) {
          const newIdentifiedRisk = {
            identified_risk: this.steps[6].fields[i].identifiedRisks,
            causes: this.steps[6].fields[i].cause,
            consequences: this.steps[6].fields[i].consequences,
            taken_measures: this.steps[6].fields[i].counterMeasures,
            status_report: this.statusReportId
          }

          axios.post('status-report-identified-risks/', newIdentifiedRisk).then((response) => {
            this.steps[6].fields[i].riskId = response.data.id
            this.showAlert()
          })
        } else {
          const patchIdentifiedRisk = {
            identified_risk: this.steps[6].fields[i].identifiedRisks,
            causes: this.steps[6].fields[i].cause,
            consequences: this.steps[6].fields[i].consequences,
            taken_measures: this.steps[6].fields[i].counterMeasures
          }

          axios.patch(`status-report-identified-risks/${this.steps[6].fields[i].riskId}/`, patchIdentifiedRisk)
          this.showAlert()
        }
      }
    },

    filterOnWeek(week) {
      this.fetchedStatusReportData = this.fetchedStatusReportData.filter((item) => parseInt(item.weekNumber) === parseInt(week))
    },

    /*setter methods*/

    setStatusReport() {
      //index 0 is enough because there will only be one status report per week
      if (this.fetchedStatusReportData.length > 0) {
        this.statusReportId = this.fetchedStatusReportData[0].id

        this.steps[0].selectedStatus = this.fetchedStatusReportData[0].global_status_level
        this.steps[0].fields[0].value = this.fetchedStatusReportData[0].global_status_comments

        this.steps[1].selectedStatus = this.fetchedStatusReportData[0].workload_level
        this.steps[1].fields[0].value = this.fetchedStatusReportData[0].workload_comments

        this.steps[2].selectedStatus = this.fetchedStatusReportData[0].quality_level
        this.steps[2].fields[0].value = this.fetchedStatusReportData[0].quality_comments

        this.steps[3].selectedStatus = this.fetchedStatusReportData[0].team_spirit_level
        this.steps[3].fields[0].value = this.fetchedStatusReportData[0].team_spirit_comments

        this.steps[4].selectedStatus = this.fetchedStatusReportData[0].schedule_level
        this.steps[4].fields[0].value = this.fetchedStatusReportData[0].schedule_comments

        this.steps[5].selectedStatus = this.fetchedStatusReportData[0].budget_level
        this.steps[5].fields[0].value = this.fetchedStatusReportData[0].budget_comments

        this.disabledStepperItems = [false, false, false, false, false, false, false]

        this.localeConsts.createEditStatusReportTitle = 'STATUSREPORTS.EDIT_STATUSREPORT_TITLE_PW'
      }
    },

    setIdentifiedStatusReportRisks() {
      if (this.fetchedStatusReportIdentifiedRisksData.length > 0) {
        this.steps[6].fields = []

        for (let i = 0; i < this.fetchedStatusReportIdentifiedRisksData.length; i++) {
          const risk = {
            name: `risk${i}`,
            riskId: this.fetchedStatusReportIdentifiedRisksData[i].id,

            identifiedRisks: this.fetchedStatusReportIdentifiedRisksData[i].identified_risk,
            cause: this.fetchedStatusReportIdentifiedRisksData[i].causes,
            counterMeasures: this.fetchedStatusReportIdentifiedRisksData[i].taken_measures,
            consequences: this.fetchedStatusReportIdentifiedRisksData[i].consequences,

            identifiedRisksPlaceholder: 'STATUSREPORTS.RISK_IDENTIFIED_RISKS_PLACEHOLDER',
            causePlaceholder: 'STATUSREPORTS.RISK_CAUSE_PLACEHOLDER',
            counterMeasuresPlaceholder: 'STATUSREPORTS.RISK_COUNTER_MEASURES_PLACEHOLDER',
            consequencesPlaceholder: 'STATUSREPORTS.RISK_CONSEQUENCES_PLACEHOLDER',

            rules: 'required|max:1024',
            isDelete: false
          }

          this.steps[6].fields.push(risk)
        }
      }
    },

    statusReportWasAlreadyCreated() {
      return this.statusReportId !== -1
    },

    identifiedRiskWasAlreadyCreated(riskId) {
      return riskId !== -1
    }
  }
}
</script>

<style lang="scss" scoped>
$text-color: #8b69a9;
$error-text-color: red;

#createEditStatusReport {
  width: 100%;
  color: $text-color;
}

/*form header*/

.formHeader {
  display: flex;
  margin-top: 15px;
  margin-bottom: 16px;
}

.currentStepTitle {
  margin-right: 15px;
}

/*buttons*/

//step buttons
.stepButtons {
  display: flex;
  justify-content: flex-end;
}

/*risk table*/

#riskTable {
  margin-top: 20px;
  margin-bottom: 30px;
}

/*messages*/
#successMessage {
  margin-top: 15px;
}

.stepErrorMessages {
  color: $error-text-color;
  font-size: 12px;
}
</style>
